import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SearchFilters, SearchResults } from '@/features/search';
import { useSearchTasks, SearchFilters as SearchFiltersType } from '@/hooks/use-search';
import { useFollowedTasks } from '@/hooks/use-tasks';
import { useDebounce } from '@/hooks/use-debounce';
import type { TaskSearchResult } from '@/types';

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState<SearchFiltersType>(() => {
    const tagIdsParam = searchParams.get('tagIds');
    return {
      q: searchParams.get('q') || '',
      projectId: searchParams.get('projectId') || undefined,
      listId: searchParams.get('listId') || undefined,
      statusId: searchParams.get('statusId') || undefined,
      assigneeId: searchParams.get('assigneeId') || undefined,
      dueBefore: searchParams.get('dueBefore') || undefined,
      dueAfter: searchParams.get('dueAfter') || undefined,
      useDateClosed: searchParams.get('useDateClosed') === 'true',
      tagIds: tagIdsParam ? tagIdsParam.split(',') : undefined,
      followingOnly: searchParams.get('followingOnly') === 'true',
    };
  });

  const [inputValue, setInputValue] = useState(filters.q || '');
  const debouncedQuery = useDebounce(inputValue, 300);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, q: debouncedQuery }));
  }, [debouncedQuery]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.q) params.set('q', filters.q);
    if (filters.projectId) params.set('projectId', filters.projectId);
    if (filters.listId) params.set('listId', filters.listId);
    if (filters.statusId) params.set('statusId', filters.statusId);
    if (filters.assigneeId) params.set('assigneeId', filters.assigneeId);
    if (filters.dueBefore) params.set('dueBefore', filters.dueBefore);
    if (filters.dueAfter) params.set('dueAfter', filters.dueAfter);
    if (filters.useDateClosed) params.set('useDateClosed', 'true');
    if (filters.tagIds?.length) params.set('tagIds', filters.tagIds.join(','));
    if (filters.followingOnly) params.set('followingOnly', 'true');

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Normal search query (disabled when followingOnly is active)
  const { data, isLoading, error, isFetching } = useSearchTasks({
    ...filters,
    q: filters.followingOnly ? undefined : filters.q,
    projectId: filters.followingOnly ? undefined : filters.projectId,
    listId: filters.followingOnly ? undefined : filters.listId,
    statusId: filters.followingOnly ? undefined : filters.statusId,
  });

  // Followed tasks query (enabled when followingOnly is active)
  const { 
    data: followedTasks = [], 
    isLoading: isLoadingFollowed 
  } = useFollowedTasks();

  // Transform followed tasks to search result format when followingOnly is active
  const followedTaskResults: TaskSearchResult[] = useMemo(() => {
    if (!filters.followingOnly) return [];
    
    return followedTasks.map((task) => ({
      id: task.id,
      projectId: task.projectId,
      listId: task.listId,
      statusId: task.statusId,
      title: task.title,
      description: task.description ?? null,
      priority: task.priority,
      dueDate: task.dueDate,
      position: task.position,
      isClientVisible: task.isClientVisible,
      completedAt: task.completedAt,
      createdBy: task.createdBy,
      createdAt: task.createdAt,
      updatedAt: task.updatedAt,
      assignees: task.assignees ?? [],
      status: task.status ? {
        id: task.status.id,
        name: task.status.name,
        color: task.status.color,
      } : { id: '', name: 'Unknown', color: '#6b7280' },
      list: task.list ?? { id: task.listId, name: 'Unknown' },
      project: task.project 
        ? { id: task.project.id, name: task.project.name, color: '#3b82f6' }
        : { id: task.projectId, name: 'Unknown', color: '#3b82f6' },
      tags: task.tags ?? [],
    }));
  }, [filters.followingOnly, followedTasks]);

  const hasActiveSearch = !!(
    filters.q?.trim() ||
    filters.projectId ||
    filters.listId ||
    filters.statusId ||
    filters.assigneeId ||
    filters.dueBefore ||
    filters.dueAfter ||
    (filters.tagIds && filters.tagIds.length > 0) ||
    filters.followingOnly
  );

  const handleClearFilters = useCallback(() => {
    setFilters({ q: filters.q });
  }, [filters.q]);

  const handleClearAll = useCallback(() => {
    setInputValue('');
    setFilters({});
  }, []);

  const hasFilters = !!(
    filters.projectId ||
    filters.listId ||
    filters.statusId ||
    filters.assigneeId ||
    filters.dueBefore ||
    filters.dueAfter ||
    (filters.tagIds && filters.tagIds.length > 0) ||
    filters.followingOnly
  );

  // Determine which results to show
  const displayResults = filters.followingOnly ? followedTaskResults : (data?.results ?? []);
  const displayTotal = filters.followingOnly ? followedTaskResults.length : (data?.total || 0);
  const displayLoading = filters.followingOnly ? isLoadingFollowed : isLoading;

  return (
    <div className="h-full p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Search</h1>
          <p className="text-muted-foreground mt-1">
            Search across all tasks you have access to
          </p>
        </div>

        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search tasks..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant={showFilters ? 'default' : 'outline'}
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
            {hasFilters && (
              <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary-foreground text-primary text-xs">
                !
              </span>
            )}
          </Button>
          {hasActiveSearch && (
            <Button variant="ghost" onClick={handleClearAll}>
              Clear
            </Button>
          )}
        </div>

        {showFilters && (
          <SearchFilters
            filters={filters}
            onFiltersChange={setFilters}
            onClear={handleClearFilters}
          />
        )}

        {isFetching && !isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Searching...
          </div>
        )}

        <SearchResults
          results={displayResults}
          total={displayTotal}
          query={filters.followingOnly ? 'Tickets I follow' : filters.q}
          isLoading={displayLoading}
          error={filters.followingOnly ? null : error}
          hasSearched={hasActiveSearch}
        />
      </div>
    </div>
  );
}
